'use client';

import { Box, HStack } from '@terra/ui-styled-system';
import {
  Text,
  IconButton,
  Badge,
  Tooltip,
  Switch,
  SegmentGroup,
} from '@terra/ui-design-system';
import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  Layout2ColumnIcon,
  Layout2RowIcon,
  Search01Icon,
} from '@terra/ui-icons';
import { useComparerStore, type ViewMode } from '../../store/comparer-store';

type ComparerToolbarProps = {
  onGoToNextDiff?: () => void;
  onGoToPrevDiff?: () => void;
  onSearch?: () => void;
};

export function ComparerToolbar({
  onGoToNextDiff,
  onGoToPrevDiff,
  onSearch,
}: ComparerToolbarProps) {
  const { settings, updateSettings, totalDiffs } = useComparerStore();
  const handleViewModeChange = (details: { value: string | null }) => {
    if (details.value) {
      updateSettings({ viewMode: details.value as ViewMode });
    }
  };

  const handlePrevDiff = () => {
    onGoToPrevDiff?.();
  };

  const handleNextDiff = () => {
    onGoToNextDiff?.();
  };

  const hasDiffs = totalDiffs > 0;

  return (
    <HStack
      w="full"
      p="1"
      borderBottomWidth="1px"
      borderColor="border.default"
      bg="bg.subtle"
      gap="2"
      alignItems="center"
      flexShrink={0}
    >
      {/* View mode selector */}
      <SegmentGroup.Root
        size="sm"
        value={settings.viewMode}
        orientation="horizontal"
        onValueChange={handleViewModeChange}
      >
        <SegmentGroup.Indicator bg="bg.muted" />
        <SegmentGroup.Item
          value="side-by-side"
          py="0.5"
          h="6"
          _hover={{ bg: 'bg.hover' }}
        >
          <SegmentGroup.ItemText fontSize="xs">
            <HStack gap="1" alignItems="center">
              <Layout2ColumnIcon size={12} />
              Side by Side
            </HStack>
          </SegmentGroup.ItemText>
          <SegmentGroup.ItemHiddenInput />
        </SegmentGroup.Item>
        <SegmentGroup.Item
          value="unified"
          py="0.5"
          h="6"
          _hover={{ bg: 'bg.hover' }}
        >
          <SegmentGroup.ItemText fontSize="xs">
            <HStack gap="1" alignItems="center">
              <Layout2RowIcon size={12} />
              Unified
            </HStack>
          </SegmentGroup.ItemText>
          <SegmentGroup.ItemHiddenInput />
        </SegmentGroup.Item>
      </SegmentGroup.Root>

      {/* Collapse unchanged */}
      <Tooltip
        title="Collapse unchanged"
        subtitle="Hide long stretches of unchanged text"
      >
        <HStack gap="1" alignItems="center">
          <Text fontSize="xs" color="fg.muted">
            Collapse
          </Text>
          <Switch
            size="sm"
            checked={settings.collapseUnchanged}
            onCheckedChange={(details) =>
              updateSettings({ collapseUnchanged: details.checked })
            }
          />
        </HStack>
      </Tooltip>

      {/* Search */}
      <Tooltip title="Search" subtitle="Find text in the diff">
        <IconButton
          size="xs"
          variant="ghost"
          aria-label="Search"
          onClick={() => onSearch?.()}
        >
          <Search01Icon size={12} />
        </IconButton>
      </Tooltip>

      {/* Spacer */}
      <Box flex="1" />

      {/* Diff navigation - Right Side */}
      {hasDiffs && (
        <>
          <Badge variant="fill">
            {totalDiffs} {totalDiffs === 1 ? 'diff' : 'diffs'}
          </Badge>
          <Tooltip title="Previous diff (↑)">
            <IconButton
              size="xs"
              variant="ghost"
              aria-label="Previous diff"
              onClick={handlePrevDiff}
            >
              <ArrowUp01Icon size={12} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Next diff (↓)">
            <IconButton
              size="xs"
              variant="ghost"
              aria-label="Next diff"
              onClick={handleNextDiff}
            >
              <ArrowDown01Icon size={12} />
            </IconButton>
          </Tooltip>
        </>
      )}
    </HStack>
  );
}
